import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import BlogShell from "./BlogShell";
import BlogCard from "./BlogCard";
import {
  BLOG_CONTENT_TYPES,
  BLOG_PAGE_SIZE,
  buildBlogUrl,
  fetchBlogArticles,
  resolveBlogImageUrl,
} from "./blogApi";
import useSeoMetadata from "./useSeoMetadata";
import { trackEvent } from "../../utils/analytics";

function BlogListing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [activeSection, setActiveSection] = useState(
    searchParams.get("section") || "all"
  );
  const [listingState, setListingState] = useState({
    articles: [],
    total: 0,
    page: 1,
    perPage: BLOG_PAGE_SIZE,
    pageCount: 1,
    loading: true,
    error: null,
  });

  const requestedPage = Math.max(1, Number(searchParams.get("page") || 1));

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
    setActiveSection(searchParams.get("section") || "all");
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    setListingState((current) => ({
      ...current,
      loading: true,
      error: null,
    }));

    fetchBlogArticles({
      page: requestedPage,
      perPage: BLOG_PAGE_SIZE,
      search: searchParams.get("q") || "",
      contentType: searchParams.get("section") || "",
    })
      .then((listing) => {
        if (cancelled) return;
        setListingState({
          ...listing,
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setListingState({
          articles: [],
          total: 0,
          page: 1,
          perPage: BLOG_PAGE_SIZE,
          pageCount: 1,
          loading: false,
          error,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [requestedPage, searchParams]);

  function updateParams(next) {
    const params = new URLSearchParams(searchParams);

    if (next.q !== undefined) {
      next.q ? params.set("q", next.q) : params.delete("q");
    }

    if (next.section !== undefined) {
      next.section && next.section !== "all"
        ? params.set("section", next.section)
        : params.delete("section");
    }

    if (next.page !== undefined) {
      Number(next.page) > 1
        ? params.set("page", String(next.page))
        : params.delete("page");
    }

    setSearchParams(params);
  }

  useSeoMetadata({
    title: "Блог Энилекс.Контроль: новости, статьи и инструкции",
    description:
      "Новости, статьи и инструкции для seller-команд на Ozon и Wildberries: контроль карточек, аналитика, реклама, отзывы, возвраты и ежедневная операционка.",
    canonicalUrl: buildBlogUrl("/blog"),
    openGraph: {
      type: "website",
      title: "Блог Энилекс.Контроль: новости, статьи и инструкции",
      description:
        "Новости, статьи и инструкции для seller-команд на Ozon и Wildberries: контроль карточек, аналитика, реклама, отзывы, возвраты и ежедневная операционка.",
      image: resolveBlogImageUrl(),
      url: buildBlogUrl("/blog"),
      siteName: "Энилекс.Контроль",
      locale: "ru_RU",
    },
    twitter: {
      card: "summary_large_image",
      title: "Блог Энилекс.Контроль: новости, статьи и инструкции",
      description:
        "Новости, статьи и инструкции для seller-команд на Ozon и Wildberries: контроль карточек, аналитика, реклама, отзывы, возвраты и ежедневная операционка.",
      image: resolveBlogImageUrl(),
    },
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Блог Энилекс.Контроль",
        url: buildBlogUrl("/blog"),
        description:
          "Редакционный хаб с новостями, статьями и инструкциями для seller-команд на маркетплейсах.",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Блог",
            item: buildBlogUrl("/blog"),
          },
        ],
      },
    ],
  });

  const currentPage = listingState.page;

  return (
    <BlogShell>
      <section className="blog-hero blog-panel" data-testid="blog-listing">
        <div className="blog-hero__kicker">Редакция Энилекс.Контроль</div>
        <h1 className="blog-hero__title">
          Новости, статьи и инструкции для seller-команд
        </h1>
        <p className="blog-hero__subtitle">
          Разбираем Ozon, Wildberries и рабочую операционку вокруг карточек,
          цен, рекламы, отзывов и возвратов. Сверху редакционные разделы,
          внутри материалов тематические категории.
        </p>
        <div className="blog-controls">
          <label className="blog-search" htmlFor="blog-search-input">
            <span className="blog-search__icon" aria-hidden="true">
              ⌕
            </span>
            <input
              id="blog-search-input"
              className="blog-search__input"
              type="search"
              placeholder="Найти материал по теме, площадке или метрике"
              value={query}
              onChange={(event) => {
                const nextValue = event.target.value;
                setQuery(nextValue);
                updateParams({ q: nextValue, page: 1 });
                trackEvent("blog_search", {
                  search_query: nextValue,
                });
              }}
            />
          </label>
          <div className="blog-results__meta">
            {listingState.total} материалов · страница {currentPage} из{" "}
            {listingState.pageCount}
          </div>
        </div>
        <div className="filter-chips" role="tablist" aria-label="Разделы блога">
          {BLOG_CONTENT_TYPES.map((section) => {
            const isActive = section.slug === activeSection;
            return (
              <button
                key={section.slug}
                type="button"
                className={`filter-chip${isActive ? " active" : ""}`}
                onClick={() => {
                  setActiveSection(section.slug);
                  updateParams({ section: section.slug, page: 1 });
                  trackEvent("blog_section_filter", {
                    blog_section: section.slug,
                  });
                }}
              >
                {section.name}
              </button>
            );
          })}
        </div>
      </section>

      <nav
        className="breadcrumbs"
        aria-label="Breadcrumbs"
        data-testid="breadcrumbs"
      >
        <Link to="/blog">Главная блога</Link>
        <span className="breadcrumbs__separator">→</span>
        <span className="breadcrumbs__current">Блог</span>
      </nav>

      {listingState.loading ? (
        <section className="blog-panel blog-status" aria-busy="true">
          <h2>Загружаем материалы</h2>
          <p>Подтягиваем последние новости, статьи и инструкции.</p>
        </section>
      ) : null}

      {listingState.error ? (
        <section className="blog-panel blog-status blog-status--error">
          <h2>Не удалось загрузить блог</h2>
          <p>Попробуйте обновить страницу. Материалы временно недоступны.</p>
        </section>
      ) : null}

      {!listingState.loading && !listingState.error ? (
        <>
          <section className="blog-grid">
            {listingState.articles.map((article) => (
              <BlogCard article={article} key={article.slug} />
            ))}
          </section>

          {listingState.articles.length === 0 ? (
            <section className="blog-panel blog-empty">
              <h2>Ничего не найдено</h2>
              <p>Попробуйте другой запрос или переключитесь на все разделы.</p>
            </section>
          ) : null}

          <nav className="pagination" aria-label="Пагинация блога">
            <button
              type="button"
              className="pagination__link"
              disabled={currentPage <= 1}
              onClick={() => updateParams({ page: currentPage - 1 })}
              aria-label="Previous page"
            >
              ←
            </button>
            {Array.from(
              { length: listingState.pageCount },
              (_, index) => index + 1
            ).map((page) => (
              <button
                key={page}
                type="button"
                className={`pagination__link${page === currentPage ? " active" : ""}`}
                onClick={() => updateParams({ page })}
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              className="pagination__link"
              disabled={currentPage >= listingState.pageCount}
              onClick={() => updateParams({ page: currentPage + 1 })}
              aria-label="Next page"
            >
              →
            </button>
          </nav>
        </>
      ) : null}
    </BlogShell>
  );
}

export default BlogListing;
